import {
  FEATURES,
  SUBSCRIPTION_TIERS,
  FeatureTier,
  TIER_CONTENT_PERMISSIONS,
  ContentPermissions,
} from "@/types/subscription";
import { PLAYER_TIER_LIMITS, PlayerTier } from "@/types/user-roles";

export class FeatureAccessService {
  /**
   * Check if user has access to a specific feature
   */
  static checkFeatureAccess(
    featureId: string,
    userTier: FeatureTier,
    currentUsage?: number
  ): {
    hasAccess: boolean;
    limit?: number;
    currentUsage?: number;
    upgradeRequired: boolean;
    recommendedTier?: FeatureTier;
    message: string;
  } {
    const feature = FEATURES.find((f) => f.id === featureId);

    if (!feature) {
      return {
        hasAccess: false,
        upgradeRequired: false,
        message: "Feature not found",
      };
    }

    const userLimit = feature.limits[userTier];

    // Check if feature is available at current tier
    if (userLimit.limit === 0) {
      const recommendedTier = this.getRecommendedTierForFeature(featureId);
      return {
        hasAccess: false,
        upgradeRequired: true,
        recommendedTier,
        message: `${feature.name} requires a ${recommendedTier} subscription`,
      };
    }

    // Check usage limits
    if (userLimit.limit && currentUsage !== undefined) {
      if (currentUsage >= userLimit.limit) {
        const recommendedTier = this.getNextTierWithMoreLimit(
          featureId,
          userTier
        );
        return {
          hasAccess: false,
          limit: userLimit.limit,
          currentUsage,
          upgradeRequired: true,
          recommendedTier,
          message: `You've reached your ${feature.name} limit (${
            userLimit.limit
          }). Upgrade to ${recommendedTier} for ${this.getTierLimitDescription(
            featureId,
            recommendedTier
          )}`,
        };
      }
    }

    return {
      hasAccess: true,
      limit: userLimit.limit,
      currentUsage,
      upgradeRequired: false,
      message: userLimit.description,
    };
  }

  /**
   * Get the minimum tier required for a feature
   */
  static getRecommendedTierForFeature(featureId: string): FeatureTier {
    const feature = FEATURES.find((f) => f.id === featureId);
    if (!feature) return "member";

    // Find the lowest tier that has this feature
    if (feature.limits.free.limit !== 0) return "free";
    return "member";
  }

  /**
   * Get the next tier that provides more access
   */
  static getNextTierWithMoreLimit(
    featureId: string,
    currentTier: FeatureTier
  ): FeatureTier {
    const feature = FEATURES.find((f) => f.id === featureId);
    if (!feature) return "member";

    if (currentTier === "free") {
      if (
        feature.limits.member.limit === undefined ||
        (feature.limits.free.limit !== undefined && 
         feature.limits.member.limit > feature.limits.free.limit)
      ) {
        return "member";
      }
      return "member";
    }

    return "member"; // Already at highest tier
  }

  /**
   * Get description of tier limits for a feature
   */
  static getTierLimitDescription(featureId: string, tier: FeatureTier): string {
    const feature = FEATURES.find((f) => f.id === featureId);
    if (!feature) return "more access";

    return feature.limits[tier].description;
  }

  /**
   * Get all features grouped by category
   */
  static getAllFeaturesByCategory() {
    const categories = {
      core: FEATURES.filter((f) => f.category === "core"),
      discovery: FEATURES.filter((f) => f.category === "discovery"),
      social: FEATURES.filter((f) => f.category === "social"),
      content: FEATURES.filter((f) => f.category === "content"),
      premium: FEATURES.filter((f) => f.category === "premium"),
    };

    return categories;
  }

  /**
   * Get highlight features for marketing
   */
  static getHighlightFeatures() {
    return FEATURES.filter((f) => (f as any).isHighlight);
  }

  /**
   * Get all features available for a specific tier
   */
  static getTierFeatures(tier: FeatureTier) {
    return FEATURES.filter((f) => f.limits[tier].limit !== 0);
  }

  /**
   * Get content permissions for a tier
   */
  static getContentPermissions(tier: FeatureTier): ContentPermissions {
    return TIER_CONTENT_PERMISSIONS[tier];
  }

  /**
   * Check if user can post content
   */
  static canPost(tier: FeatureTier): boolean {
    return TIER_CONTENT_PERMISSIONS[tier].canPost;
  }

  /**
   * Check if user can view social feed
   */
  static canViewSocialFeed(tier: FeatureTier): boolean {
    return TIER_CONTENT_PERMISSIONS[tier].canViewCommunityPosts;
  }

  /**
   * Check if user can tag entities in posts
   */
  static canTagEntities(tier: FeatureTier): boolean {
    return TIER_CONTENT_PERMISSIONS[tier].canTagCourses;
  }

  /**
   * Get tier limits for display
   */
  static getTierLimits(tier: FeatureTier) {
    const tierData = SUBSCRIPTION_TIERS.find((t) => t.id === tier);
    return tierData?.limits || null;
  }

  /**
   * Format price for display
   */
  static formatPrice(cents: number, period: "monthly" | "yearly"): string {
    const dollars = cents / 100;
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(dollars);

    if (period === "yearly") {
      const monthlyEquivalent = cents / 100 / 12;
      return `${formatted}/year (${new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(monthlyEquivalent)}/month)`;
    }

    return `${formatted}/month`;
  }

  /**
   * Get upgrade message for a blocked feature
   */
  static getUpgradeMessage(
    featureId: string,
    currentTier: FeatureTier
  ): string {
    const feature = FEATURES.find((f) => f.id === featureId);
    if (!feature) return "Upgrade to access this feature";

    const recommendedTier = this.getRecommendedTierForFeature(featureId);
    const tierData = SUBSCRIPTION_TIERS.find((t) => t.id === recommendedTier);
    
    if (currentTier === "free") {
      return `Upgrade to ${tierData?.name || "Member"} to ${feature.description.toLowerCase()}`;
    }
    
    return `Upgrade to Member for ${feature.limits.member.description.toLowerCase()}`;
  }

  /**
   * Check if upgrading would benefit the user for a specific feature
   */
  static wouldUpgradeBenefit(
    featureId: string,
    currentTier: FeatureTier
  ): boolean {
    if (currentTier === "member") return false;
    
    const feature = FEATURES.find((f) => f.id === featureId);
    if (!feature) return false;

    const currentLimit = feature.limits[currentTier];
    const nextTier = "member";
    const nextLimit = feature.limits[nextTier];

    // Check if next tier has better access
    if (currentLimit.limit === 0 && nextLimit.limit !== 0) return true;
    if (currentLimit.limit !== undefined && nextLimit.limit === undefined) return true;
    if (currentLimit.limit !== undefined && nextLimit.limit !== undefined && 
        nextLimit.limit > currentLimit.limit) return true;

    return false;
  }

  /**
   * Get comparison data between tiers
   */
  static getTierComparison() {
    return SUBSCRIPTION_TIERS.map((tier) => ({
      id: tier.id,
      name: tier.name,
      description: tier.description,
      price: tier.monthlyPrice,
      yearlyPrice: tier.yearlyPrice,
      features: this.getTierFeatures(tier.id),
      limits: tier.limits,
      contentPermissions: TIER_CONTENT_PERMISSIONS[tier.id],
      isPopular: tier.isPopular,
    }));
  }
}
